import { TraceDataDetail, TraceDataInfo } from 'types/purchase';
import { OperatorType } from './Enums';
import { TFunction } from 'i18next';
import { DATE_FORMAT } from './ConstantValues';
import { hideLastCharacters, normalizeNumber } from './commons';
import _snakeCase from 'lodash/snakeCase';
import dayjs from 'dayjs';

export type SiteEntry = {
  stageIndex: number;
  stage: string;
  info: TraceDataInfo;
};

export type SiteMap = Map<string, SiteEntry>;
export type SuccessorsMap = Map<string, string[]>;

/**
 * Unique key for a graph node — combines siteId with stageIndex so the same
 * siteId appearing in multiple stages is treated as distinct nodes.
 */
export const nodeKey = (siteId: string, stageIndex: number): string =>
  `${siteId}::${stageIndex}`;

/** Build siteId → sorted-ascending stageIndices from all stages. */
function buildSiteStages(stages: TraceDataDetail[]): Map<string, number[]> {
  const map = new Map<string, number[]>();
  for (const s of stages) {
    for (const site of s.sites) {
      const arr = map.get(site.siteId) ?? [];
      arr.push(s.stageIndex);
      map.set(site.siteId, arr);
    }
  }
  return map;
}

/**
 * Returns the stageIndex of `siteId` that is the closest predecessor of
 * `currentStageIndex` (i.e. highest stageIndex strictly less than current).
 */
function predecessorStageIndex(
  siteId: string,
  currentStageIndex: number,
  siteStages: Map<string, number[]>
): number | undefined {
  return (siteStages.get(siteId) ?? [])
    .filter((si) => si < currentStageIndex)
    .sort((a, b) => b - a)[0];
}

export function buildSiteMap(stages: TraceDataDetail[]): SiteMap {
  const map: SiteMap = new Map();
  for (const s of stages) {
    for (const site of s.sites) {
      map.set(nodeKey(site.siteId, s.stageIndex), {
        stageIndex: s.stageIndex,
        stage: s.stage,
        info: site,
      });
    }
  }
  return map;
}

export function buildSuccessorsMap(stages: TraceDataDetail[]): SuccessorsMap {
  const siteStages = buildSiteStages(stages);
  const map: SuccessorsMap = new Map();
  for (const s of stages) {
    for (const site of s.sites) {
      const successorKey = nodeKey(site.siteId, s.stageIndex);
      for (const f of site.fromSites) {
        const fromSI = predecessorStageIndex(f.id, s.stageIndex, siteStages);
        if (fromSI === undefined) continue;
        const predecessorKey = nodeKey(f.id, fromSI);
        const list = map.get(predecessorKey) ?? [];
        list.push(successorKey);
        map.set(predecessorKey, list);
      }
    }
  }
  return map;
}

/**
 * Returns the set of compound node keys (nodeKey format) reachable from
 * `startKey` in both directions of the graph.
 */
export function findConnectedSiteIds(
  startKey: string,
  siteMap: SiteMap,
  successorsMap: SuccessorsMap
): Set<string> {
  const visited = new Set<string>([startKey]);

  // Forward pass: follow successors (compound keys).
  const forwardQueue = [startKey];
  while (forwardQueue.length) {
    const cur = forwardQueue.shift();
    if (!cur) continue;
    for (const s of successorsMap.get(cur) ?? []) {
      if (!visited.has(s)) {
        visited.add(s);
        forwardQueue.push(s);
      }
    }
  }

  // Backward pass: follow fromSites, resolving to compound keys.
  // Derive siteStages from siteMap so we don't need an extra parameter.
  const siteStages = new Map<string, number[]>();
  siteMap.forEach((entry) => {
    const arr = siteStages.get(entry.info.siteId) ?? [];
    arr.push(entry.stageIndex);
    siteStages.set(entry.info.siteId, arr);
  });

  const backwardQueue = [startKey];
  while (backwardQueue.length) {
    const cur = backwardQueue.shift();
    if (!cur) continue;
    const entry = siteMap.get(cur);
    if (entry) {
      for (const f of entry.info.fromSites) {
        const fromSI = predecessorStageIndex(
          f.id,
          entry.stageIndex,
          siteStages
        );
        if (fromSI === undefined) continue;
        const fromKey = nodeKey(f.id, fromSI);
        if (!visited.has(fromKey)) {
          visited.add(fromKey);
          backwardQueue.push(fromKey);
        }
      }
    }
  }

  return visited;
}

/** Returns [fromNodeKey, toNodeKey] compound-key pairs for drawing lines. */
export function buildEdges(
  connected: Set<string>,
  stages: TraceDataDetail[]
): [string, string][] {
  const siteStages = buildSiteStages(stages);
  const edges: [string, string][] = [];
  const seen = new Set<string>();
  for (const s of stages) {
    for (const site of s.sites) {
      const toKey = nodeKey(site.siteId, s.stageIndex);
      if (!connected.has(toKey)) continue;
      for (const f of site.fromSites) {
        const fromSI = predecessorStageIndex(f.id, s.stageIndex, siteStages);
        if (fromSI === undefined) continue;
        const fromKey = nodeKey(f.id, fromSI);
        if (!connected.has(fromKey)) continue;
        const edgeKey = `${fromKey}|${toKey}`;
        if (!seen.has(edgeKey)) {
          seen.add(edgeKey);
          edges.push([fromKey, toKey]);
        }
      }
    }
  }
  return edges;
}

export const getTraceDetailCsv = (params: {
  detail: Omit<TraceDataDetail, 'status'>;
  t: TFunction<'translation', undefined, 'translation'>;
}) => {
  const { detail, t } = params;

  const emissionHeader: [string, string][] = [
    [
      'deliveredDatetime',
      t('purchase_page.trace_data_detail.delivered_datetime'),
    ],
    [
      'deliveryToSiteName',
      t('purchase_page.trace_data_detail.delivery_to_site_name'),
    ],
    [
      'receivedDatetime',
      t('purchase_page.trace_data_detail.received_datetime'),
    ],
    ['description', t('purchase_page.trace_data_detail.description')],
    ['weight', t('purchase_page.trace_data_detail.weight')],
    ['id', t('purchase_page.trace_data_detail.id')],
  ];

  if (detail.stage === OperatorType.Emissions) {
    return {
      headers: emissionHeader,
      data: detail.sites.map((item) =>
        item.data.map((it) => ({
          deliveredDatetime: it.deliveredDatetime
            ? dayjs(it.deliveredDatetime).format(DATE_FORMAT.slaYMDHm)
            : '',
          deliveryToSiteName: it.deliveryToSiteName,
          receivedDatetime: it.collectedDatetime
            ? dayjs(it.collectedDatetime).format(DATE_FORMAT.slaYMDHm)
            : '',
          description: it.description,
          weight: normalizeNumber({ value: it.weight }),
          id: hideLastCharacters(it.id),
        }))
      ),
    };
  }

  if (detail.stage === OperatorType.Purchase) {
    const purchaseHeader: [string, string][] = [
      ['operatorName', t('purchase_page.trace_data_detail.operator_name')],
      ['siteName', t('purchase_page.trace_data_detail.site_name')],
      [
        'procurementDate',
        t('purchase_page.trace_data_detail.procurement_date'),
      ],
      ['sender', t('purchase_page.trace_data_detail.sender')],
      ['description', t('purchase_page.trace_data_detail.material_name')],
      ['materialType', t('purchase_page.trace_data_detail.material_type')],
      ['grade', t('purchase_page.trace_data_detail.grade')],
      ['weight', t('purchase_page.trace_data_detail.weight')],
      ['id', t('purchase_page.trace_data_detail.procure_id')],
    ];

    return {
      headers: purchaseHeader,
      data: detail.sites.map((item) =>
        item.data.map((it) => ({
          operatorName: it.operatorName,
          siteName: it.siteName,
          procurementDate: it.procurementDate
            ? dayjs(it.procurementDate).format(DATE_FORMAT.slaYMDHm)
            : '',
          sender: it.sender,
          description: it.description,
          materialType: it.materialType,
          grade: it.grade,
          weight: normalizeNumber({ value: it.weight }),
          id: it.id,
        }))
      ),
    };
  }

  const isNotMiddleProcess = detail.stageIndex % 1 === 0;

  const commonHeader: [string, string][] = [
    ['operatorName', t('purchase_page.trace_data_detail.operator_name')],
    ['siteName', t('purchase_page.trace_data_detail.site_name')],
    ['registDatetime', t('purchase_page.trace_data_detail.regist_datetime')],
    [
      'deliveredDatetime',
      t('purchase_page.trace_data_detail.delivered_datetime'),
    ],
    [
      'deliveryToSiteName',
      t('purchase_page.trace_data_detail.delivery_to_site_name'),
    ],
    [
      'requestedDatetime',
      t('purchase_page.trace_data_detail.requested_datetime'),
    ],
    ['sender', t('purchase_page.trace_data_detail.sender')],
    ['description', t('purchase_page.trace_data_detail.material_name')],
    ...(isNotMiddleProcess
      ? ([
          ['materialType', t('purchase_page.trace_data_detail.material_type')],
        ] as [string, string][])
      : []),
    ['grade', t('purchase_page.trace_data_detail.grade')],
    ['weight', t('purchase_page.trace_data_detail.weight')],
    ['id', t('purchase_page.trace_data_detail.id')],
    ...(isNotMiddleProcess
      ? ([['inputIds', t('purchase_page.trace_data_detail.resource_id')]] as [
          string,
          string
        ][])
      : []),
  ];

  return {
    headers: commonHeader,
    data: detail.sites.map((item) =>
      item.data.map((it) => ({
        operatorName: it.operatorName,
        siteName: it.siteName,
        registDatetime: it.registDatetime
          ? dayjs(it.registDatetime).format(DATE_FORMAT.slaYMDHm)
          : '',
        deliveredDatetime: it.deliveredDatetime
          ? dayjs(it.deliveredDatetime).format(DATE_FORMAT.slaYMDHm)
          : '',
        deliveryToSiteName:
          detail.stageIndex % 1 === 0 ? it.deliveryToSiteName : it.siteName,
        requestedDatetime: it.bookingDatetime
          ? dayjs(it.bookingDatetime).format(DATE_FORMAT.slaYMDHm)
          : '',
        sender: it.siteName,
        description: it.description,
        materialType: it.materialType,
        grade: it.grade,
        weight: normalizeNumber({ value: it.weight }),
        id: it.id,
        inputIds: it.inputIds?.join('\n'),
      }))
    ),
  };
};

export const getTraceDetailCsvFilename = (params: {
  id: string;
  stage: OperatorType;
  stageIndex: number;
  t: TFunction<'translation', undefined, 'translation'>;
}) => {
  const { id, stage, stageIndex, t } = params;

  let stageName = t(
    `purchase_page.trace_data_detail.stages.${_snakeCase(stage)}`
  );

  if (stageIndex % 1 !== 0) {
    if (stage === OperatorType.Compressions) {
      stageName = t(
        'purchase_page.trace_data_detail.stages.middle_compression'
      );
    } else if (stage === OperatorType.Recycle) {
      stageName = t('purchase_page.trace_data_detail.stages.middle_recycle');
    } else {
      stageName = `${t(
        'purchase_page.trace_data_detail.middle_process'
      )} (${stageName})`;
    }
  }

  return `${id}_${stageName}`;
};
